import React, { useEffect, useState } from "react";
import { X, ArrowRight, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const STORAGE_KEY = "if_lead_capture_v1";

const GOALS = [
  "Buy a home",
  "Buy a car",
  "Repair my credit",
  "Get approved for a loan",
  "Build business credit",
  "Other",
];

export default function LeadCaptureModal() {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [goal, setGoal] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (loading) return;
    if (user) return; // skip for logged-in users
    const p = window.location.pathname;
    if (["/login", "/register", "/forgot-password", "/reset-password", "/sign/"].some((x) => p.startsWith(x))) return;
    const seen = localStorage.getItem(STORAGE_KEY);
    if (seen) return;
    // small delay so it doesn't fight the page paint
    const t = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(t);
  }, [user, loading]);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ at: Date.now(), status: done ? "submitted" : "skipped" }));
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !goal) {
      setError("Email and goal are required.");
      return;
    }
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      setError("Please enter a valid phone number so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { email, name, phone, goal });
      setDone(true);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ at: Date.now(), status: "submitted" }));
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center px-4"
      data-testid="lead-capture-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-capture-title"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-md card-dark border-[var(--if-gold)]/40 p-7 sm:p-8 animate-[fadeIn_0.25s_ease-out]">
        <button
          onClick={close}
          className="absolute top-3 right-3 text-white/40 hover:text-white p-2"
          data-testid="lead-capture-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {!done ? (
          <>
            <div className="font-mono-label text-[var(--if-gold)] mb-2">Before You Look Around</div>
            <h2 id="lead-capture-title" className="font-display text-2xl sm:text-3xl leading-tight mb-3">
              What do you want to <span className="italic text-[var(--if-gold)]">achieve</span>?
            </h2>
            <p className="text-sm text-white/65 mb-5" style={{ fontFamily: "Inter, sans-serif" }}>
              Tell us your goal and a real credit specialist will personally reach out — no spam.
            </p>

            <form onSubmit={submit} className="flex flex-col gap-3" data-testid="lead-capture-form">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-black/30 border border-white/15 focus:border-[var(--if-gold)]/60 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/35 outline-none transition-colors"
                data-testid="lead-capture-name"
              />
              <input
                type="email"
                placeholder="Email address *"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black/30 border border-white/15 focus:border-[var(--if-gold)]/60 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/35 outline-none transition-colors"
                data-testid="lead-capture-email"
              />
              <input
                type="tel"
                placeholder="Phone number *"
                required
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full bg-black/30 border border-white/15 focus:border-[var(--if-gold)]/60 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/35 outline-none transition-colors"
                data-testid="lead-capture-phone"
              />
              <select
                required
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                className="w-full bg-black/30 border border-white/15 focus:border-[var(--if-gold)]/60 rounded-lg px-4 py-3 text-sm text-white outline-none transition-colors"
                data-testid="lead-capture-goal"
              >
                <option value="" disabled>What's your #1 goal? *</option>
                {GOALS.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>

              {error && (
                <div className="text-xs text-red-400" data-testid="lead-capture-error">{error}</div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full mt-2"
                data-testid="lead-capture-submit"
              >
                {submitting ? "Sending…" : <>Enter the site <ArrowRight className="w-4 h-4" /></>}
              </button>

              <button
                type="button"
                onClick={close}
                className="text-xs text-white/40 hover:text-white/70 mt-1 underline-offset-2 hover:underline self-center"
                data-testid="lead-capture-skip"
              >
                Skip for now
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-2">
            <div className="w-14 h-14 rounded-full bg-[var(--if-gold)]/15 border border-[var(--if-gold)]/40 flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-7 h-7 text-[var(--if-gold)]" />
            </div>
            <h2 className="font-display text-2xl mb-2">You're in.</h2>
            <p className="text-sm text-white/65 mb-4" style={{ fontFamily: "Inter, sans-serif" }}>
              We got your info. A real human from Infantry Financial will reach out shortly.
            </p>
            <a
              href="/ebook"
              className="flex items-center gap-3 bg-[#EFEDE5] border border-[#4F5D1E]/25 rounded-xl p-3 mb-4 text-left hover:border-[#4F5D1E]/60 transition-all"
              data-testid="lead-capture-ebook-offer"
            >
              <img src="https://static.prod-images.emergentagent.com/jobs/2e360db4-79c7-4489-9f27-5d85af163a96/images/b43c371e8463ba141c8605960e0db1a95b47346879a778d3926e2f7807500787.jpeg" alt="From Denied to Approved ebook" className="w-11 rounded shadow shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold tracking-widest uppercase text-[#4F5D1E]" style={{ fontFamily: "Inter, sans-serif" }}>While you wait · $4.99</p>
                <p className="text-sm font-semibold text-[#23261C] leading-snug" style={{ fontFamily: "Inter, sans-serif" }}>Get the "From Denied to Approved" roadmap ebook →</p>
              </div>
            </a>
            <button
              onClick={close}
              className="btn-gold w-full"
              data-testid="lead-capture-continue"
            >
              Continue to site <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
